import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { MapView } from '../../components/common/MapView';
import { Button } from '../../components/ui/button';
import { Pause, Play, Square, Wifi, WifiOff, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDuration } from '../../lib/formatters';
import { useGpsTracker } from '../../lib/use-gps';
import { useWakeLock } from '../../lib/use-wake-lock';
import { useAuth } from '../../lib/auth-context';
import { useUnits, kmToDisplay, unitLabel, paceSecPerUnit } from '../../lib/units';
import { loadRunSession, saveRunSession, clearRunSession, elapsedSec } from '../../lib/run-session';

export default function LivePersonalRun() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const isFresh = search.get('fresh') === '1';
  const { units } = useUnits();
  const { profile } = useAuth();

  // Resume-or-start on mount.
  const initialSession = useMemo(() => {
    if (isFresh) {
      clearRunSession();
      return null;
    }
    const existing = loadRunSession();
    if (existing && existing.type === 'personal') return existing;
    return null;
  }, [isFresh]);

  const isResume = initialSession != null;
  // Baseline (fixed for THIS mount only — represents everything already tracked
  // in previous mounts of the same session).
  const baselineDistanceRef = useRef(initialSession?.distanceMeters || 0);
  const baselinePathRef = useRef(initialSession?.path || []);

  const [session, setSession] = useState(initialSession);
  // Skip countdown when resuming — user has already started.
  const [countdown, setCountdown] = useState(initialSession ? 0 : 3);
  const [paused, setPaused] = useState(initialSession?.pausedAt != null);
  const [, forceTick] = useState(0);

  const running = countdown === 0 && !paused;
  const gps = useGpsTracker({ active: running });
  const wake = useWakeLock(countdown === 0);

  // First-tracking-frame toast (only when starting fresh — not on resume).
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (running && !toastFiredRef.current) {
      toastFiredRef.current = true;
      if (!isResume && wake.supported && wake.active) {
        toast.success('Screen will stay on — your run keeps tracking.', { duration: 4500 });
      } else if (!isResume && !wake.supported) {
        toast.message('Tip: keep your screen unlocked or GPS will pause.', { duration: 4500 });
      } else if (isResume) {
        toast.success('Run resumed.', { duration: 3000 });
      }
    }
  }, [running]);

  // Countdown tick.
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Create session at countdown-0 if none exists.
  useEffect(() => {
    if (countdown > 0 || session) return;
    const s = {
      type: 'personal',
      startedAt: Date.now(),
      pausedAt: null,
      accumulatedPauseMs: 0,
      distanceMeters: 0,
      path: [],
    };
    saveRunSession(s);
    setSession(s);
  }, [countdown, session]);

  // Wall-clock tick — force re-render every second so `elapsed` display updates.
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(i);
  }, [running]);

  // Cumulative distance + path (baseline + this-mount GPS delta).
  const cumulativeDistanceMeters = baselineDistanceRef.current + gps.distanceMeters;
  const cumulativePath = useMemo(
    () => [...baselinePathRef.current, ...gps.path],
    [gps.path]
  );

  // Persist to localStorage on every GPS update or pause toggle.
  useEffect(() => {
    if (!session || countdown > 0) return;
    saveRunSession({
      ...session,
      distanceMeters: cumulativeDistanceMeters,
      path: cumulativePath,
      pausedAt: paused ? (session.pausedAt || Date.now()) : null,
    });
  }, [cumulativeDistanceMeters, cumulativePath.length, paused]);

  function handlePause() {
    setPaused(true);
    if (session) {
      const next = { ...session, pausedAt: Date.now() };
      saveRunSession(next);
      setSession(next);
    }
  }
  function handleResume() {
    setPaused(false);
    if (session && session.pausedAt) {
      const paused_for = Date.now() - session.pausedAt;
      const next = {
        ...session,
        pausedAt: null,
        accumulatedPauseMs: (session.accumulatedPauseMs || 0) + paused_for,
      };
      saveRunSession(next);
      setSession(next);
    }
  }

  // Wall-clock elapsed — survives tab background / screen lock.
  const elapsed = session
    ? elapsedSec(
        { ...session, pausedAt: paused ? (session.pausedAt || Date.now()) : null },
        Date.now()
      )
    : 0;
  const distanceKm = cumulativeDistanceMeters / 1000;
  // Pace sanity: don't display until we've moved at least 50 m (avoids the
  // "0.02 km in 12 s = 600 min/km" nonsense during GPS warmup), and hide any
  // implausibly slow pace (> 25 min/km ≈ 2.4 km/h — slower than a stroll).
  const MIN_DISTANCE_FOR_PACE_M = 50;
  const MAX_DISPLAYABLE_PACE_SEC_PER_KM = 25 * 60;
  const paceKmSecRaw = cumulativeDistanceMeters >= MIN_DISTANCE_FOR_PACE_M && elapsed > 0
    ? Math.floor(elapsed / distanceKm)
    : null;
  const paceKmSec = paceKmSecRaw && paceKmSecRaw <= MAX_DISPLAYABLE_PACE_SEC_PER_KM
    ? paceKmSecRaw
    : null;
  const paceUnitSec = paceKmSec ? Math.round(paceSecPerUnit(paceKmSec, units)) : null;
  const paceLabel = paceUnitSec ? `${Math.floor(paceUnitSec / 60)}:${String(paceUnitSec % 60).padStart(2, '0')}` : '--:--';
  const distanceDisplay = kmToDisplay(distanceKm, units).toFixed(2);

  // "Locating slowly" hint after 10s without a fix.
  const [slowGps, setSlowGps] = useState(false);
  useEffect(() => {
    if (gps.accuracy != null) { setSlowGps(false); return; }
    const t = setTimeout(() => setSlowGps(true), 10000);
    return () => clearTimeout(t);
  }, [gps.accuracy]);

  async function finish() {
    sessionStorage.setItem('rivl:run:elapsed', String(elapsed));
    sessionStorage.setItem('rivl:run:distance', String(distanceKm));
    sessionStorage.setItem('rivl:run:track', JSON.stringify(cumulativePath));
    // Persist the ACTUAL wall-clock start of the run (survives navigation +
    // reload). This is what RunSummary and personal_runs.date should show —
    // without it we'd stamp the moment the row was inserted, not the moment
    // the runner actually started running.
    if (session?.startedAt) {
      sessionStorage.setItem('rivl:run:startedAt', String(session.startedAt));
    }
    clearRunSession();
    nav('/run/summary');
  }

  const hasRealPath = cumulativePath.length > 1;
  const displayPath = hasRealPath ? cumulativePath : [];
  const currentIdx = hasRealPath ? cumulativePath.length - 1 : 0;
  const acquiring = gps.supported && !gps.error && gps.accuracy == null;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground relative" data-testid="live-personal-run">
      <div className="px-4 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div>
          <p className="rivl-label">Personal Run{isResume ? ' · Resumed' : ''}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Tracking · no verification</p>
        </div>
        <div className="flex items-center gap-2">
          {wake.active && (
            <div className="flex items-center gap-1 text-[10px] text-success font-mono" data-testid="wake-lock-active" title="Screen will stay on">
              <Lock className="h-3 w-3" /> SCREEN ON
            </div>
          )}
          {gps.supported && !gps.error ? (
            acquiring ? (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono" data-testid="gps-acquiring">
                <Wifi className="h-3 w-3 animate-pulse" /> ACQUIRING
              </div>
            ) : (
              <div className={`flex items-center gap-1 text-[10px] font-mono ${gps.accuracy < 15 ? 'text-success' : gps.accuracy < 40 ? 'text-primary' : 'text-destructive'}`}>
                <Wifi className="h-3 w-3" /> ±{Math.round(gps.accuracy)}m
              </div>
            )
          ) : (
            <div className="flex items-center gap-1 text-[10px] text-destructive font-mono">
              <WifiOff className="h-3 w-3" /> GPS OFF
            </div>
          )}
        </div>
      </div>

      {gps.error && (
        <p className="text-[11px] text-destructive px-4 pt-2" data-testid="run-gps-error">
          GPS error: {gps.error}. Allow location access in your browser settings.
        </p>
      )}

      {acquiring && !gps.error && !slowGps && (
        <p className="text-[11px] text-muted-foreground px-4 pt-2" data-testid="run-gps-locating">
          Locating you… make sure location permission is allowed.
        </p>
      )}

      {acquiring && !gps.error && slowGps && (
        <p className="text-[11px] text-destructive px-4 pt-2" data-testid="run-gps-slow">
          Still locating — move outdoors and away from buildings for a faster GPS lock.
        </p>
      )}

      <div className="px-4 py-6 grid grid-cols-3 gap-3">
        <Stat label="Distance" value={distanceDisplay} unit={unitLabel(units)} testid="run-distance" />
        <Stat label="Time" value={formatDuration(elapsed)} testid="run-time" />
        <Stat label="Pace" value={paceLabel} unit={`/${unitLabel(units)}`} testid="run-pace" />
      </div>

      <div className="px-4 flex-1">
        <MapView
          height={320}
          path={displayPath}
          showCurrent={hasRealPath}
          currentIndex={currentIdx}
          showUserLocation
          userAvatarUrl={profile?.avatar_url}
        />
      </div>

      <div className="px-4 py-4 grid grid-cols-3 gap-3">
        {paused ? (
          <Button
            onClick={handleResume}
            data-testid="run-resume-btn"
            className="h-14 rounded-md flex items-center gap-2 text-base font-semibold bg-success hover:bg-success/90 text-white"
            disabled={countdown > 0}
          >
            <Play className="h-5 w-5" /> Resume
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={handlePause}
            data-testid="run-pause-btn"
            className="h-14 rounded-md flex items-center gap-2 text-base font-semibold"
            disabled={countdown > 0}
          >
            <Pause className="h-5 w-5" /> Pause
          </Button>
        )}
        <Button
          onClick={finish}
          data-testid="run-finish-btn"
          className="h-14 rounded-md flex items-center gap-2 text-base font-semibold bg-foreground text-background hover:bg-foreground/90 col-span-2"
          disabled={countdown > 0}
        >
          <Square className="h-5 w-5" /> Finish
        </Button>
      </div>

      {countdown > 0 && (
        <div
          className="absolute inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center"
          data-testid="run-countdown"
        >
          <div className="rivl-label text-[10px] mb-3">Get ready</div>
          <div
            key={countdown}
            className="font-display text-[140px] font-black leading-none text-primary tabular-nums animate-pulse"
          >
            {countdown}
          </div>
          <div className="mt-4 text-xs text-muted-foreground">Run starts in {countdown}…</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, unit, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="rivl-label text-[10px]">{label}</div>
      <div className="font-mono font-bold text-3xl tracking-tight tabular-nums mt-1">{value}</div>
      {unit && <div className="rivl-label text-[9px]">{unit}</div>}
    </div>
  );
}
